import React, { useEffect, useState, useCallback } from "react";

/**
 * KeyboardShortcuts — global keyboard listener + help overlay.
 *
 * Bindings:
 *   Cmd/Ctrl+K   focus chatbar (any element with data-vella-chatbar="true")
 *   Cmd/Ctrl+/   toggle voice mode (dispatches vella:toggleVoice; navigates to talkToVella first if needed)
 *   ?            open shortcuts help
 *   Esc          close any open modal (dispatches vella:escape, closes help if open)
 *
 * The "?" floating button in the bottom-right is the visible affordance
 * for users who don't read shortcuts pages.
 */

const SHORTCUTS = [
  { keys: ["⌘", "K"], altKeys: ["Ctrl", "K"], desc: "Focus the chatbar" },
  { keys: ["⌘", "/"], altKeys: ["Ctrl", "/"], desc: "Toggle voice mode" },
  { keys: ["?"], desc: "Show this help" },
  { keys: ["Esc"], desc: "Close any open modal" },
];

function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

function focusChatbar() {
  // Prefer a chatbar inside the currently visible page first.
  const candidates = document.querySelectorAll('[data-vella-chatbar="true"]');
  for (const el of candidates) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      el.focus();
      return true;
    }
  }
  return false;
}

export default function KeyboardShortcuts({ getActive, setActive }) {
  const [helpOpen, setHelpOpen] = useState(false);

  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const openHelp = useCallback(() => setHelpOpen(true), []);

  useEffect(() => {
    function onKey(e) {
      const meta = e.metaKey || e.ctrlKey;
      const target = e.target;

      // Cmd/Ctrl+K → focus chatbar
      if (meta && !e.shiftKey && !e.altKey && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        focusChatbar();
        return;
      }

      // Cmd/Ctrl+/ → toggle voice mode
      if (meta && !e.shiftKey && !e.altKey && e.key === "/") {
        e.preventDefault();
        const active = typeof getActive === "function" ? getActive() : null;
        if (active !== "talkToVella" && typeof setActive === "function") {
          setActive("talkToVella");
          // Defer dispatch until TalkToVella is mounted.
          setTimeout(() => window.dispatchEvent(new CustomEvent("vella:toggleVoice")), 60);
        } else {
          window.dispatchEvent(new CustomEvent("vella:toggleVoice"));
        }
        return;
      }

      // Esc → close help, then dispatch global escape so modals can close
      if (e.key === "Escape") {
        if (helpOpen) {
          closeHelp();
          return;
        }
        window.dispatchEvent(new CustomEvent("vella:escape"));
        return;
      }

      // ? → open help (require Shift form on US layouts; ignore while typing)
      if (e.key === "?" && !meta && !isTypingTarget(target)) {
        e.preventDefault();
        openHelp();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [getActive, setActive, helpOpen, closeHelp, openHelp]);

  return (
    <>
      {/* Floating help button */}
      <button
        type="button"
        onClick={openHelp}
        data-vella-chrome="help"
        aria-label="Keyboard shortcuts"
        title="Keyboard shortcuts (?)"
        style={{
          position: "fixed",
          right: 18,
          bottom: 90,
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.55)",
          background: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          color: "#0A1B3A",
          fontFamily: "inherit",
          fontSize: 16,
          fontWeight: 700,
          cursor: "pointer",
          zIndex: 600,
          boxShadow: "0 4px 16px -4px rgba(11,107,211,0.25), 0 1px 0 rgba(255,255,255,0.85) inset",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 200ms ease, background 200ms ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        ?
      </button>

      {helpOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard shortcuts"
          onClick={closeHelp}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 700,
            background: "rgba(10, 27, 58, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            animation: "vellaShortcutFade 0.18s ease-out",
          }}
        >
          <style>{`
            @keyframes vellaShortcutFade {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
          `}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(30px) saturate(180%)",
              WebkitBackdropFilter: "blur(30px) saturate(180%)",
              border: "1px solid rgba(255,255,255,0.6)",
              borderRadius: 22,
              padding: "22px 26px",
              boxShadow: "0 20px 60px -20px rgba(11,107,211,0.35), 0 1px 0 rgba(255,255,255,0.9) inset",
              fontFamily: "inherit",
              color: "#0A1B3A",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, letterSpacing: "-0.022em" }}>
                Keyboard shortcuts
              </h3>
              <button
                type="button"
                onClick={closeHelp}
                aria-label="Close"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  border: "none",
                  background: "rgba(10,27,58,0.06)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0A1B3A",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {SHORTCUTS.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <span style={{ fontSize: 13, color: "#384559" }}>{s.desc}</span>
                  <span style={{ display: "inline-flex", gap: 4 }}>
                    {s.keys.map((k, j) => (
                      <kbd
                        key={j}
                        style={{
                          minWidth: 24,
                          padding: "3px 7px",
                          fontSize: 11,
                          fontWeight: 700,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          color: "#0A1B3A",
                          background: "rgba(255,255,255,0.85)",
                          border: "1px solid rgba(10,27,58,0.18)",
                          borderRadius: 6,
                          textAlign: "center",
                          boxShadow: "0 1px 0 rgba(10,27,58,0.12)",
                        }}
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: "#5a7a9a", margin: "16px 0 0", textAlign: "center" }}>
              On Windows / Linux, ⌘ is Ctrl.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
